/**
 * `<SaveDialog>` — name-input dialog for the "Save New" flow.
 *
 * Architecture §9.4 / UX §5.2 State 6 / Story FFCS-FFCS-E003-S013.
 *
 * Composition:
 *   - `lib/ui/dialog` shim (kept aligned with the shadcn Dialog API).
 *   - Siesa UI Kit `Input` (label, placeholder, error state built-in).
 *   - Siesa UI Kit `Button` for Cancel (outline) and Save (filled).
 *   - lucide-react `Loader2` spinner while a mutation is in-flight.
 *   - lucide-react `X` for the close button.
 *   - `useFormCacheSelectorSchema` — Zod schema with i18n-bound messages.
 *
 * Behavior:
 *   - Auto-focused, single-line text input. Max length is 51 to allow Zod to
 *     report the over-50 error rather than silently truncating.
 *   - Character counter rendered as `{n} / 50` right-aligned below the input.
 *   - On submit:
 *       1. Run Zod validation locally — show inline error and short-circuit
 *          if invalid.
 *       2. Call `onSubmit(name)`. The parent hook handles the API call,
 *          409 surfacing, success banner, and dialog dismissal.
 *   - When the user types, both the local validation error and any server
 *     error (e.g. 409) are cleared so the user can retry without confusion.
 *   - `serverError` is a translation key supplied by the parent (e.g.
 *     `errors.nameAlreadyExists`). The dialog renders it through `t(...)`.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import { Button } from '@components/Button/Button';
import { Input } from '@components/Input/Input';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../lib/ui/dialog';
import { useFormCacheSelectorSchema } from '../FormCacheSelector.schema';

export interface SaveDialogProps {
  /** Whether the dialog is currently visible. Controlled by the parent. */
  open: boolean;
  /** Toggle handler — invoked when the dialog wants to close (e.g. cancel). */
  onOpenChange: (open: boolean) => void;
  /**
   * Submit handler. Receives the trimmed user-supplied name. The parent is
   * responsible for the actual upsert call, the 409 → serverError mapping,
   * and dismissing the dialog on success.
   */
  onSubmit: (name: string) => Promise<void>;
  /** True while any mutation is in-flight — disables submit, shows spinner. */
  isMutating: boolean;
  /**
   * Translation key for a server-side error (typically a 409 conflict) to
   * render inline. `null` when there is no server error to show.
   */
  serverError: string | null;
  /** Clears the server error — called as the user starts editing the input. */
  onServerErrorClear: () => void;
}

export function SaveDialog({
  open,
  onOpenChange,
  onSubmit,
  isMutating,
  serverError,
  onServerErrorClear,
}: SaveDialogProps) {
  const { t } = useTranslation('form-cache-selector');
  const schema = useFormCacheSelectorSchema();

  const [name, setName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName('');
      setLocalError(null);
    }
  }, [open]);

  const handleChange = (value: string) => {
    setName(value);
    if (localError !== null) setLocalError(null);
    if (serverError !== null) onServerErrorClear();
  };

  const runSubmit = async () => {
    const trimmed = name.trim();
    const result = schema.safeParse({ name: trimmed });
    if (!result.success) {
      const firstIssue = result.error.issues[0];
      setLocalError(firstIssue?.message ?? t('validation.name.required'));
      return;
    }
    setLocalError(null);
    await onSubmit(trimmed);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await runSubmit();
  };

  const errorMessage =
    localError !== null
      ? localError
      : serverError !== null
        ? t(serverError)
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="save-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <DialogTitle>{t('saveDialog.title')}</DialogTitle>
            <DialogClose data-testid="save-dialog-close">
              <X className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t('saveDialog.cancel')}</span>
            </DialogClose>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-4">
          <Input
            autoFocus
            data-testid="save-dialog-name-input"
            label={t('saveDialog.nameLabel')}
            placeholder={t('saveDialog.namePlaceholder')}
            value={name}
            onChange={(e) => handleChange(e.target.value)}
            maxLength={51}
            required
            error={errorMessage !== null}
          />
          <div className="mt-1 flex items-start justify-between text-xs">
            {errorMessage !== null ? (
              <span role="alert" data-testid="save-dialog-error" className="text-red-600">
                {errorMessage}
              </span>
            ) : (
              <span />
            )}
            <span data-testid="char-counter" className="text-gray-400">
              {name.length} / 50
            </span>
          </div>
          <DialogFooter>
            <Button
              htmlType="button"
              type="outline"
              size="sm"
              data-testid="save-dialog-cancel"
              onClick={() => onOpenChange(false)}
              disabled={isMutating}
            >
              {t('saveDialog.cancel')}
            </Button>
            <Button
              htmlType="button"
              size="sm"
              data-testid="save-dialog-submit"
              disabled={isMutating}
              onClick={() => { void runSubmit(); }}
              leftIcon={
                isMutating ? (
                  <Loader2 className="h-4 w-4 animate-spin" data-testid="save-spinner" />
                ) : undefined
              }
            >
              {t('saveDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
